/**
 * Input — minimal SYSTEM-05 §D1 surface. Label + error + hint slots
 * supported as direct children rendered above/below. T2.3+ may grow this
 * to support prefix/suffix icons; today's auth forms don't need them.
 */
import { forwardRef, useId, type InputHTMLAttributes } from "react";

import { cn } from "../../lib/cn";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { className, label, error, hint, id, containerClassName, ...rest },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const describedById = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

    return (
      <div className={cn("flex flex-col gap-1.5", containerClassName)}>
        {label !== undefined && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-neutral-700"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error !== undefined ? "true" : undefined}
          aria-describedby={describedById}
          className={cn(
            "h-10 w-full rounded-md border bg-white px-3 text-sm text-neutral-900 placeholder:text-neutral-400",
            "transition-colors duration-150",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
            "disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-400",
            error
              ? "border-error-400 focus-visible:ring-error-500"
              : "border-neutral-300",
            className,
          )}
          {...rest}
        />
        {error !== undefined && (
          <p id={`${inputId}-error`} className="text-xs text-error-600">
            {error}
          </p>
        )}
        {error === undefined && hint !== undefined && (
          <p id={`${inputId}-hint`} className="text-xs text-neutral-500">
            {hint}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";
